import { parseBesselianElements, SolarEclipse } from '@astronomy-bundle/solar-eclipse';
import { TimeOfInterest } from '@astronomy-bundle/core';

import { ECLIPSE_2026_BESSELIAN_ELEMENTS } from './eclipseElements';
import type {
  Coordinate,
  ElevatedCoordinate,
  GeoJsonFeature,
  GeoJsonFeatureCollection,
  GeoJsonLineString,
} from '../types';

export const ECLIPSE_DATE = '2026-08-12';
export const ECLIPSE_TIMELINE_START = new Date('2026-08-12T17:20:00Z');
export const ECLIPSE_TIMELINE_END = new Date('2026-08-12T19:40:00Z');
export const ECLIPSE_PREVIEW_TIME = new Date('2026-08-12T18:29:30Z');

const elements = parseBesselianElements(ECLIPSE_2026_BESSELIAN_ELEMENTS);
const eclipse = SolarEclipse.createFromBesselianElements(elements);

const asLine = (points: Coordinate[]): GeoJsonFeature<GeoJsonLineString> => ({
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'LineString',
    coordinates: points.map(({ lat, lon }) => [lon, lat]),
  },
});

const allCentralLine = eclipse.getCentralLine({ refraction: true, stepsInSeconds: 20 });

export const eclipseMapData: GeoJsonFeatureCollection<
  GeoJsonLineString | { type: 'Polygon'; coordinates: Array<Array<[number, number]>> },
  { kind: 'totality' | 'centerline' }
> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: { kind: 'totality' },
      geometry: {
        type: 'Polygon',
        coordinates: [
          eclipse
            .getUmbraPathPolygon({ refraction: true, stepsInSeconds: 20 })
            .map(({ lat, lon }) => [lon, lat]),
        ],
      },
    },
    { ...asLine(allCentralLine), properties: { kind: 'centerline' } },
  ],
};

export type EclipsePhase = 'before' | 'partial' | 'total' | 'after';

export interface ContactTimes {
  c1: Date;
  c2: Date | null;
  maximum: Date;
  c3: Date | null;
  c4: Date;
}

export interface LocalEclipseDetails {
  type: 'none' | 'partial' | 'total' | 'annular';
  magnitude: number;
  obscuration: number;
  durationSeconds: number;
  centralDurationSeconds: number;
  contacts: ContactTimes | null;
  sunAltitude: number | null;
  sunAzimuth: number | null;
}

export interface LocalEclipseMoment {
  phase: EclipsePhase;
  obscuration: number;
  magnitude: number;
}

export function getLocalEclipseDetails(location: ElevatedCoordinate): LocalEclipseDetails {
  const local = eclipse.getLocalEclipse(location);
  const contacts = local.getContactTimes();
  const maximum = contacts?.max;
  const horizontal = maximum
    ? local.getCircumstances(maximum).getApparentTopocentricHorizontalCoordinates()
    : null;

  return {
    type: local.getType(),
    magnitude: local.getMaxMagnitude(),
    obscuration: local.getMaxObscuration(),
    durationSeconds: local.getDuration(),
    centralDurationSeconds: local.getCentralDuration(),
    contacts: contacts
      ? {
          c1: contacts.c1.getDate(),
          c2: contacts.c2?.getDate() ?? null,
          maximum: contacts.max.getDate(),
          c3: contacts.c3?.getDate() ?? null,
          c4: contacts.c4.getDate(),
        }
      : null,
    sunAltitude: horizontal?.altitude ?? null,
    sunAzimuth: horizontal?.azimuth ?? null,
  };
}

export function getEclipsePhase(details: LocalEclipseDetails, at: Date): EclipsePhase {
  const contacts = details.contacts;
  if (!contacts || at < contacts.c1) return 'before';
  if (at > contacts.c4) return 'after';
  if (contacts.c2 && contacts.c3 && at >= contacts.c2 && at <= contacts.c3) return 'total';
  return 'partial';
}

export function getLocalEclipseMoment(location: ElevatedCoordinate, at: Date): LocalEclipseMoment {
  const local = eclipse.getLocalEclipse(location);
  const contacts = local.getContactTimes();
  if (!contacts) return { phase: 'before', obscuration: 0, magnitude: 0 };
  const c1 = contacts.c1.getDate();
  const c4 = contacts.c4.getDate();
  if (at < c1) return { phase: 'before', obscuration: 0, magnitude: 0 };
  if (at > c4) return { phase: 'after', obscuration: 0, magnitude: 0 };
  const c2 = contacts.c2?.getDate() ?? null;
  const c3 = contacts.c3?.getDate() ?? null;
  const phase = c2 && c3 && at >= c2 && at <= c3 ? 'total' : 'partial';
  const circumstances = local.getCircumstances(TimeOfInterest.fromDate(at));
  return {
    phase,
    obscuration: Math.max(0, Math.min(1, circumstances.getObscuration())),
    magnitude: Math.max(0, circumstances.getMagnitude()),
  };
}

export function getCountdownParts(now: Date): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  complete: boolean;
} {
  const difference = Math.max(0, ECLIPSE_PREVIEW_TIME.getTime() - now.getTime());
  return {
    days: Math.floor(difference / 86_400_000),
    hours: Math.floor((difference % 86_400_000) / 3_600_000),
    minutes: Math.floor((difference % 3_600_000) / 60_000),
    seconds: Math.floor((difference % 60_000) / 1000),
    complete: difference === 0,
  };
}

export const eclipseReference = {
  greatest: eclipse.getLocationOfGreatestEclipse(),
  greatestTime: eclipse.getTimeOfGreatestEclipse().getDate(),
  maximumCentralDurationSeconds: eclipse.getMaxCentralDuration(),
};
